/**
 * @module RecruitingDetailUnified
 * @description Unified Bewerber-Detail — wraps RecruitingDetail (fest) and FlexBewerberDetail (flex)
 *   with a shared header bar showing type badge, quick status, and unified back navigation.
 *   Auto-detects bewerber type via API, then delegates to the appropriate detail component.
 * @route /recruiting/bewerber/:id
 * @since v2.246.0
 * @updated v2.247.0 — Proper unified header with type badge, pipeline badge, back nav
 */
import { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams, useNavigate } from 'react-router-dom';
import { useLanguage } from '../context/LanguageContext';
import RecruitingDetail from './RecruitingDetail';
import FlexBewerberDetail from './FlexBewerberDetail';
import BackLink from '../components/BackLink';

const API = import.meta.env.VITE_API_URL || '';
const f = (url) => fetch(`${API}${url}`, { credentials: 'include' });

// #959 Bundle A: Status = Single Source of Truth (gleicher Spec wie RecruitingBewerberUnified).
// Aliase: in_pruefung→in_review, eingegangen→beworben.
const STATUS_COLORS = {
  beworben:   { color: '#338edc', bg: '#EBF4FD' },
  in_review:  { color: '#F59E0B', bg: '#FEF3C7' },
  eingeladen: { color: '#22D3EE', bg: '#CFFAFE' },
  zugesagt:   { color: '#22D3EE', bg: '#CFFAFE' },
  aktiv:      { color: '#10B981', bg: '#D1FAE5' },
  abgelehnt:  { color: '#EF4444', bg: '#FEE2E2' },
  inaktiv:    { color: '#9CA3AF', bg: '#F3F4F6' },
};
const STATUS_ALIASES = { in_pruefung: 'in_review', eingegangen: 'beworben' };
const canonicalStatus = (s) => {
  if (!s) return null;
  const stripped = s.replace(/^flex_/, '');
  return STATUS_ALIASES[stripped] || stripped;
};

export default function RecruitingDetailUnified() {
  const { id } = useParams();
  const { t } = useLanguage();
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [typ, setTyp] = useState(searchParams.get('typ') || null);
  const [loading, setLoading] = useState(!typ);
  const [bewerberMeta, setBewerberMeta] = useState(null);

  const detect = useCallback(async () => {
    if (typ) {
      try {
        const endpoint = typ === 'flex'
          ? `/api/flex/bewerber/${id}`
          : `/api/recruiting/bewerber/${id}`;
        const res = await f(endpoint);
        if (res.ok) {
          const data = await res.json();
          const b = data.bewerber || data;
          setBewerberMeta({
            vorname: b.vorname,
            nachname: b.nachname,
            status: canonicalStatus(b.status),
            stelle_titel: b.stelle_titel || b.position || null,
          });
        }
      } catch { /* header will be minimal */ }
      setLoading(false);
      return;
    }
    try {
      const res = await f(`/api/recruiting/bewerber/${id}`);
      if (res.ok) {
        const data = await res.json();
        const b = data.bewerber;
        if (b?.kategorie === 'flex') {
          setTyp('flex');
        } else {
          setTyp('fest');
          setBewerberMeta({
            vorname: b?.vorname,
            nachname: b?.nachname,
            status: canonicalStatus(b?.status),
            stelle_titel: b?.stelle_titel || b?.position,
          });
        }
      } else {
        const flexRes = await f(`/api/flex/bewerber/${id}`);
        if (flexRes.ok) {
          const data = await flexRes.json();
          const b = data.bewerber || data;
          setTyp('flex');
          setBewerberMeta({
            vorname: b.vorname,
            nachname: b.nachname,
            status: canonicalStatus(b.status),
            stelle_titel: null,
          });
        } else {
          setTyp('fest');
        }
      }
    } catch {
      setTyp('fest');
    }
    setLoading(false);
  }, [id, typ]);

  useEffect(() => { detect(); }, [detect]);

  if (loading) {
    return (
      <div className="p-8 text-center" style={{ color: 'var(--text-tertiary, #6B7280)' }}>
        <div className="inline-block w-6 h-6 border-2 border-[#338edc] border-t-transparent rounded-full animate-spin" />
      </div>
    );
  }

  const isFlex = typ === 'flex';
  const meta = bewerberMeta;
  const statusMeta = meta?.status ? (STATUS_COLORS[meta.status] || { color: '#6B7280', bg: '#F3F4F6' }) : null;

  return (
    <div className="flex flex-col" style={{ minHeight: 'calc(100vh - 3.5rem)' }}>
      {/* ─── Unified Header Bar ─── */}
      <div className="px-6 pt-5 pb-3 border-b flex-shrink-0" style={{ borderColor: 'var(--border-card, #E5E7EB)' }}>
        <div className="flex items-center justify-between flex-wrap gap-3">
          {/* Left: Back + Name + Type Badge */}
          <div className="flex items-center gap-3 min-w-0">
            <BackLink to="/recruiting/bewerber" label={t('recruiting.bewerber.title', 'Bewerber')} className="flex-shrink-0" />
            {meta && (
              <>
                <span style={{ color: 'var(--text-muted, #9CA3AF)' }}>/</span>
                <h1 className="text-base font-bold truncate" style={{ color: 'var(--text-primary, #1A1A2E)' }}>
                  {meta.vorname} {meta.nachname}
                </h1>
              </>
            )}
            {/* Type Badge */}
            <span className="text-[10px] px-2 py-0.5 rounded-full font-semibold flex-shrink-0"
              style={{
                background: isFlex ? '#ECFEFF' : '#EBF4FD',
                color: isFlex ? '#0891B2' : '#338edc',
              }}>
              {isFlex ? 'Flex-Worker' : t('recruiting.bewerber.fest', 'Festanstellung')}
            </span>
          </div>
          {/* Right: Status Badge + Position */}
          <div className="flex items-center gap-2 flex-shrink-0">
            {meta?.stelle_titel && (
              <span className="text-xs px-2.5 py-1 rounded-lg border hidden sm:inline-block"
                style={{ borderColor: 'var(--border-card, #E5E7EB)', color: 'var(--text-secondary, #4B5563)' }}>
                {meta.stelle_titel}
              </span>
            )}
            {meta?.status && statusMeta && (
              <span className="text-xs font-semibold px-2.5 py-1 rounded-full"
                style={{ background: statusMeta.bg, color: statusMeta.color }}>
                {t(`recruiting.status.${meta.status}`, meta.status.charAt(0).toUpperCase() + meta.status.slice(1).replace(/_/g, ' '))}
              </span>
            )}
          </div>
        </div>
      </div>

      {/* ─── Delegated Detail Component ─── */}
      {isFlex ? <FlexBewerberDetail /> : <RecruitingDetail />}
    </div>
  );
}
